import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { usersAPI, specialtiesAPI, Specialty } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PlusIcon,
  SearchIcon,
  UserIcon,
  EyeIcon,
  EditIcon,
  MailIcon,
  ChevronLeftIcon,
  UserCheckIcon,
  UserXIcon,
  TrashLinesIcon,
  ChevronDownIcon,
  FilterIcon,
} from '../icons';

// Canadian provinces/territories (mirrors the list used in UserSettings).
const PROVINCES: { value: string; label: string }[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

interface User {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  role: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
  active: boolean;
  salary?: number | null;
  hours_per_week?: number | null;
  province_code?: string | null;
  specialty_ids?: number[];
  invitation_pending?: boolean;
  invitation_sent_at?: string;
  invitation_accepted_at?: string;
  organization: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
  last_login?: string;
}

interface InviteData {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
}

const CONTRIBUTOR_ROLE_VALUES = ['developer', 'analyst', 'designer', 'qa_engineer'];

const roleLabel = (t: any, role: string) =>
  t(`roles.${role}`, { defaultValue: role });

const employmentTypeLabel = (t: any, employmentType?: string) =>
  t(`users.${employmentType || 'employee'}`, {
    defaultValue: employmentType || 'employee',
  });

// Premium Icons
const Icons = {
  plus: <PlusIcon className="w-5 h-5" strokeWidth={2} />,
  search: <SearchIcon className="w-5 h-5" strokeWidth={1.5} />,
  user: <UserIcon className="w-5 h-5" strokeWidth={1.5} />,
  eye: <EyeIcon className="w-4 h-4" strokeWidth={1.5} />,
  edit: <EditIcon className="w-4 h-4" strokeWidth={1.5} />,
  mail: <MailIcon className="w-4 h-4" strokeWidth={1.5} />,
  chevronLeft: <ChevronLeftIcon className="w-5 h-5" strokeWidth={1.5} />,
  userCheck: <UserCheckIcon className="w-4 h-4" strokeWidth={1.5} />,
  userX: <UserXIcon className="w-4 h-4" strokeWidth={1.5} />,
  trash: <TrashLinesIcon className="w-4 h-4" strokeWidth={1.5} />,
  chevronDown: <ChevronDownIcon className="w-4 h-4" strokeWidth={2} />,
  filter: <FilterIcon className="w-4 h-4" strokeWidth={2} />,
};

const UserList: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState<number[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [userLimit, setUserLimit] = useState<number | null>(null);
  const [activeUserCount, setActiveUserCount] = useState(0);

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    fetchUsers();
    specialtiesAPI
      .getAll()
      .then((r) => setSpecialties(r.data || []))
      .catch(() => setSpecialties([]));
  }, []);

  const toggleSpecialtyFilter = (skillId: number) => {
    setSpecialtyFilter((prev) =>
      prev.includes(skillId)
        ? prev.filter((x) => x !== skillId)
        : [...prev, skillId],
    );
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
      setUserLimit(response.data.user_limit ?? null);
      setActiveUserCount(response.data.active_user_count ?? 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    const confirmed = await confirm({
      title: t('users.deactivateTitle'),
      message: t('users.confirmDeactivate'),
      confirmLabel: t('users.deactivate'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await usersAPI.update(id, { active: false });
      setUsers(users.map((u) => (u.id === id ? { ...u, active: false } : u)));
      setActiveUserCount((prev) => prev - 1);
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      showToast(t('users.deactivateFailed'), 'error');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await usersAPI.update(id, { active: true });
      setUsers(users.map((u) => (u.id === id ? { ...u, active: true } : u)));
      setActiveUserCount((prev) => prev + 1);
    } catch (error: any) {
      console.error('Failed to activate user:', error);
      const msg = error.response?.data?.error || t('users.activateFailed');
      showToast(msg, 'error');
    }
  };

  const handleResendInvite = async (id: number) => {
    try {
      await usersAPI.resendInvitation(id);
      showToast(t('users.inviteResent'), 'success');
    } catch (error: any) {
      console.error('Failed to resend invite:', error);
      showToast(
        error.response?.data?.details?.[0] || t('users.resendFailed'),
        'error',
      );
    }
  };

  const handleDelete = async (id: number) => {
    const user = users.find((u) => u.id === id);
    const isPending = user?.invitation_pending;

    const confirmed = await confirm({
      title: t('users.deleteTitle'),
      message: isPending
        ? t('users.confirmDeletePending')
        : t('users.confirmDelete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await usersAPI.delete(id);
      setUsers(users.filter((u) => u.id !== id));
      showToast(t('users.deleteSuccess'), 'success');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      const errorMsg = error.response?.data?.error || '';
      if (
        errorMsg.includes('foreign key') ||
        errorMsg.includes('constraint') ||
        error.response?.status === 422
      ) {
        showToast(t('users.deleteHasDataError'), 'error');
      } else {
        showToast(errorMsg || t('users.deleteFailed'), 'error');
      }
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    const isPending = !!user.invitation_pending;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'active' && user.active && !isPending) ||
      (statusFilter === 'inactive' && !user.active) ||
      (statusFilter === 'pending' && isPending);

    const matchesSpecialty =
      specialtyFilter.length === 0 ||
      specialtyFilter.some((sid) => (user.specialty_ids || []).includes(sid));

    return matchesSearch && matchesRole && matchesStatus && matchesSpecialty;
  });

  const limitReached = userLimit !== null && activeUserCount >= userLimit;

  const getRoleColor = (role: string) => {
    const isContributorRole = CONTRIBUTOR_ROLE_VALUES.includes(role);

    if (isDark) {
      switch (role) {
        case 'admin':
          return 'bg-violet-500/20 text-violet-400 border border-violet-500/30';
        case 'manager':
          return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
        case 'developer':
        case 'analyst':
        case 'designer':
        case 'qa_engineer':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        default:
          return isContributorRole
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
            : 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    }
    switch (role) {
      case 'admin':
        return 'bg-violet-100 text-violet-700';
      case 'manager':
        return 'bg-cyan-100 text-cyan-700';
      case 'developer':
      case 'analyst':
      case 'designer':
      case 'qa_engineer':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return isContributorRole
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-slate-100 text-slate-700';
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1
              className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('users.title')}
            </h1>
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-lg ${
                limitReached
                  ? isDark
                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                    : 'bg-rose-100 text-rose-700'
                  : isDark
                    ? 'bg-slate-700/50 text-slate-400'
                    : 'bg-slate-100 text-slate-600'
              }`}
            >
              {userLimit !== null
                ? t('users.userCount', {
                    count: activeUserCount,
                    limit: userLimit,
                  })
                : t('users.unlimitedUsers')}
            </span>
          </div>
          <p
            className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {filteredUsers.length} users total
          </p>
        </div>
        {limitReached ? (
          <span
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl cursor-not-allowed ${
              isDark
                ? 'bg-slate-700/50 text-slate-500'
                : 'bg-slate-200 text-slate-400'
            }`}
            title={t('users.userLimitReached', {
              count: activeUserCount,
              limit: userLimit,
            })}
          >
            {Icons.plus}
            <span>{t('users.inviteUser')}</span>
          </span>
        ) : (
          <Link
            to="/manager/users/invite"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.plus}
            <span>{t('users.inviteUser')}</span>
          </Link>
        )}
      </div>

      {limitReached && (
        <div
          className={`p-3 rounded-xl text-sm ${
            isDark
              ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
              : 'bg-amber-50 border border-amber-200 text-amber-800'
          }`}
        >
          {t('users.userLimitReached', {
            count: activeUserCount,
            limit: userLimit,
          })}
        </div>
      )}

      {/* Filters */}
      <div className={`${cardClass} rounded-xl p-4`}>
        {/* Search - always visible */}
        <div className="relative">
          <span
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {Icons.search}
          </span>
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 ${inputClass}`}
          />
        </div>

        {/* Mobile filter toggle */}
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`md:hidden w-full flex items-center justify-between mt-3 py-2 px-3 rounded-xl transition-colors ${
            isDark
              ? 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {Icons.filter}
            <span className="text-sm font-medium">{t('common.filters')}</span>
            {(roleFilter || statusFilter) && (
              <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-violet-500 text-white">
                {[roleFilter, statusFilter].filter(Boolean).length +
                  (specialtyFilter.length > 0 ? 1 : 0)}
              </span>
            )}
          </div>
          <span
            className={`transform transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
          >
            {Icons.chevronDown}
          </span>
        </button>

        {/* Filter dropdowns - collapsible on mobile */}
        <div className={`${filtersOpen ? 'block' : 'hidden'} md:block mt-3`}>
          <div className="flex flex-col md:flex-row gap-3 md:mb-3">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className={`w-full md:w-auto mb-3 md:mb-0 ${inputClass}`}
            >
              <option value="">{t('users.allRoles')}</option>
              <option value="admin">{t('roles.admin')}</option>
              <option value="manager">{t('roles.manager')}</option>
              <option value="developer">{t('roles.developer')}</option>
              <option value="analyst">{t('roles.analyst')}</option>
              <option value="designer">{t('roles.designer')}</option>
              <option value="qa_engineer">{t('roles.qa_engineer')}</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full md:w-auto ${inputClass}`}
            >
              <option value="">{t('users.allStatus')}</option>
              <option value="active">{t('projects.active')}</option>
              <option value="inactive">{t('projects.inactive')}</option>
              <option value="pending">{t('users.invitationPending')}</option>
            </select>
          </div>
          {specialties.length > 0 && (
            <div className="mt-3">
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('users.skills', { defaultValue: 'Skills' })}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {specialties.map((specialty) => (
                  <label
                    key={specialty.id}
                    className={`flex items-center gap-2 cursor-pointer text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    <input
                      type="checkbox"
                      checked={specialtyFilter.includes(specialty.id)}
                      onChange={() => toggleSpecialtyFilter(specialty.id)}
                      className="rounded border-slate-400 text-violet-500 focus:ring-violet-500 focus:ring-offset-0"
                    />
                    {specialty.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <div className={`${cardClass} rounded-2xl p-12 text-center`}>
          <div
            className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}
          >
            <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
              {Icons.user}
            </span>
          </div>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            {t('users.noUsers')}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredUsers.map((user) => (
              <div key={user.id} className={`${cardClass} rounded-2xl p-4`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-violet-500 to-purple-600">
                      <span className="text-sm font-semibold text-white">
                        {user.first_name[0]}
                        {user.last_name[0]}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div
                        className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {user.full_name}
                      </div>
                      <div
                        className={`text-xs break-all ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                      >
                        {user.email}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getRoleColor(user.role)}`}
                  >
                    {roleLabel(t, user.role)}
                  </span>
                  {user.invitation_pending ? (
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                        isDark
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {t('users.invitationPending')}
                    </span>
                  ) : (
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                        user.active
                          ? isDark
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-emerald-100 text-emerald-700'
                          : isDark
                            ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                            : 'bg-rose-100 text-rose-700'
                      }`}
                    >
                      {user.active
                        ? t('projects.active')
                        : t('projects.inactive')}
                    </span>
                  )}
                </div>

                {(user.specialty_ids || []).length > 0 && specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(user.specialty_ids || []).map((sid) => {
                      const specialty = specialties.find((s) => s.id === sid);
                      if (!specialty) return null;
                      return (
                        <span
                          key={sid}
                          className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}
                        >
                          {specialty.name}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div
                  className={`text-xs mb-3 space-y-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  <div>
                    {t('users.lastLogin')}:{' '}
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString()
                      : t('users.never')}
                  </div>
                  <div>
                    {t('users.joined')}:{' '}
                    {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div
                  className={`flex items-center gap-2 pt-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                >
                  <Link
                    to={`/manager/users/${user.id}`}
                    className={`p-2 rounded-xl transition-all ${
                      isDark
                        ? 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'
                        : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
                    }`}
                    title={t('common.view')}
                  >
                    {Icons.eye}
                  </Link>
                  <Link
                    to={`/manager/users/${user.id}/edit`}
                    className={`p-2 rounded-xl transition-all ${
                      isDark
                        ? 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                        : 'bg-cyan-100 text-cyan-600 hover:bg-cyan-200'
                    }`}
                    title={t('common.edit')}
                  >
                    {Icons.edit}
                  </Link>
                  {user.active ? (
                    <button
                      onClick={() => handleDeactivate(user.id)}
                      className={`p-2 rounded-xl transition-all ${
                        isDark
                          ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                          : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                      }`}
                      title={t('users.deactivate')}
                    >
                      {Icons.userX}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(user.id)}
                      disabled={limitReached}
                      className={`p-2 rounded-xl transition-all ${
                        limitReached
                          ? isDark
                            ? 'bg-slate-700/30 text-slate-600 cursor-not-allowed'
                            : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                          : isDark
                            ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                            : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                      }`}
                      title={
                        limitReached
                          ? t('users.userLimitReached', {
                              count: activeUserCount,
                              limit: userLimit,
                            })
                          : t('users.activate')
                      }
                    >
                      {Icons.userCheck}
                    </button>
                  )}
                  {user.invitation_pending && (
                    <button
                      onClick={() => handleResendInvite(user.id)}
                      className={`p-2 rounded-xl transition-all ${
                        isDark
                          ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                          : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                      }`}
                      title={t('users.resend')}
                    >
                      {Icons.mail}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(user.id)}
                    className={`p-2 rounded-xl transition-all ${
                      isDark
                        ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                        : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                    }`}
                    title={t('common.delete')}
                  >
                    {Icons.trash}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div
            className={`hidden md:block ${cardClass} rounded-2xl overflow-hidden`}
          >
            <div
              ref={tableContainerRef}
              className={`overflow-auto max-h-[calc(100vh-450px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <table className="w-full relative">
                <thead
                  className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900/80' : 'bg-slate-50'}`}
                >
                  <tr>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('users.user')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('users.role')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('common.status')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('users.lastLogin')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('users.joined')}
                    </th>
                    <th
                      className={`px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
                >
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={`transition-colors ${isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-purple-600">
                            <span className="text-sm font-semibold text-white">
                              {user.first_name[0]}
                              {user.last_name[0]}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div
                              className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {user.full_name}
                            </div>
                            <div
                              className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {user.email}
                            </div>
                            {(user.specialty_ids || []).length > 0 &&
                              specialties.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(user.specialty_ids || []).map((sid) => {
                                    const specialty = specialties.find(
                                      (s) => s.id === sid,
                                    );
                                    if (!specialty) return null;
                                    return (
                                      <span
                                        key={sid}
                                        className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}
                                      >
                                        {specialty.name}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getRoleColor(user.role)}`}
                        >
                          {roleLabel(t, user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {user.invitation_pending && (
                            <span
                              className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                                isDark
                                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {t('users.invitationPending')}
                            </span>
                          )}
                          {!user.invitation_pending && (
                            <span
                              className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                                user.active
                                  ? isDark
                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                    : 'bg-emerald-100 text-emerald-700'
                                  : isDark
                                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                    : 'bg-rose-100 text-rose-700'
                              }`}
                            >
                              {user.active
                                ? t('projects.active')
                                : t('projects.inactive')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : t('users.never')}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/manager/users/${user.id}`}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'hover:bg-slate-700 text-cyan-400' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.view')}
                          >
                            {Icons.eye}
                          </Link>
                          <Link
                            to={`/manager/users/${user.id}/edit`}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.edit')}
                          >
                            {Icons.edit}
                          </Link>
                          {user.active ? (
                            <button
                              onClick={() => handleDeactivate(user.id)}
                              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-amber-500/20 text-amber-700 hover:text-amber-400' : 'hover:bg-amber-50 text-slate-500 hover:text-amber-600'}`}
                              title={t('users.deactivate')}
                            >
                              {Icons.userX}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              disabled={limitReached}
                              className={
                                limitReached
                                  ? 'p-2 rounded-lg text-slate-400 cursor-not-allowed'
                                  : 'p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all'
                              }
                              title={
                                limitReached
                                  ? t('users.userLimitReached', {
                                      count: activeUserCount,
                                      limit: userLimit,
                                    })
                                  : t('users.activate')
                              }
                            >
                              {Icons.userCheck}
                            </button>
                          )}
                          {user.invitation_pending && (
                            <button
                              onClick={() => handleResendInvite(user.id)}
                              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-purple-500/20 text-slate-400 hover:text-purple-400' : 'hover:bg-purple-50 text-slate-500 hover:text-purple-600'}`}
                              title={t('users.resend')}
                            >
                              {Icons.mail}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(user.id)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-rose-500/20 text-slate-400 hover:text-rose-400' : 'hover:bg-rose-50 text-slate-500 hover:text-rose-600'}`}
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const InviteUser: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user: currentUser } = useAuth();
  const [inviteData, setInviteData] = useState<InviteData>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'developer',
    employment_type: 'employee',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const getAllowedRoles = () => {
    if (currentUser?.role === 'admin') {
      return [
        { value: 'developer', label: t('roles.developer') },
        { value: 'analyst', label: t('roles.analyst') },
        { value: 'designer', label: t('roles.designer') },
        { value: 'qa_engineer', label: t('roles.qa_engineer') },
        { value: 'manager', label: t('roles.manager') },
        { value: 'admin', label: t('roles.admin') },
      ];
    } else if (currentUser?.role === 'manager') {
      return [
        { value: 'developer', label: t('roles.developer') },
        { value: 'analyst', label: t('roles.analyst') },
        { value: 'designer', label: t('roles.designer') },
        { value: 'qa_engineer', label: t('roles.qa_engineer') },
      ];
    }
    return [];
  };

  const allowedRoles = getAllowedRoles();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.invite({
        email: inviteData.email,
        first_name: inviteData.first_name,
        last_name: inviteData.last_name,
        role: inviteData.role,
        employment_type: inviteData.employment_type,
      });
      setSuccess(t('users.invitationSent'));
      setTimeout(() => {
        navigate('/manager/users');
      }, 2000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.details?.join(', ') ||
        error.response?.data?.error ||
        t('users.inviteFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/manager/users')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.chevronLeft}
          </button>
          <h1
            className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('users.inviteNewUser')}
          </h1>
        </div>

        {error && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-xl text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-xl text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`${cardClass} p-6 space-y-5`}>
            <div>
              <label className={labelClass}>{t('users.emailAddress')} *</label>
              <input
                type="email"
                required
                value={inviteData.email}
                onChange={(e) =>
                  setInviteData({ ...inviteData, email: e.target.value })
                }
                className={inputClass}
                placeholder="user@example.com"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('auth.firstName')} *</label>
                <input
                  type="text"
                  required
                  value={inviteData.first_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, first_name: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{t('auth.lastName')} *</label>
                <input
                  type="text"
                  required
                  value={inviteData.last_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, last_name: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('users.role')} *</label>
                <select
                  required
                  value={inviteData.role}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, role: e.target.value })
                  }
                  className={inputClass}
                >
                  {allowedRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <p
                  className={`mt-2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {currentUser?.role === 'manager'
                    ? t('users.managerCanOnlyInviteDevelopers')
                    : t('users.chooseRoleDescription')}
                </p>
              </div>

              <div>
                <label className={labelClass}>
                  {t('users.employmentType')} *
                </label>
                <select
                  required
                  value={inviteData.employment_type}
                  onChange={(e) =>
                    setInviteData({
                      ...inviteData,
                      employment_type: e.target.value as
                        | 'employee'
                        | 'contractor'
                        | 'intern'
                        | 'part_time'
                        | 'consultant',
                    })
                  }
                  className={inputClass}
                >
                  <option value="employee">{t('users.employee')}</option>
                  <option value="contractor">{t('users.contractor')}</option>
                  <option value="intern">{t('users.intern')}</option>
                  <option value="part_time">{t('users.part_time')}</option>
                  <option value="consultant">{t('users.consultant')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className={`${cardClass} p-4`}>
            <h3
              className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('users.rolePermissions')}
            </h3>
            <ul
              className={`text-xs space-y-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              <li>
                <strong
                  className={isDark ? 'text-slate-300' : 'text-slate-700'}
                >
                  {t('roles.developer')}:
                </strong>{' '}
                {t('users.developerPermissions')}
              </li>
              <li>
                <strong
                  className={isDark ? 'text-slate-300' : 'text-slate-700'}
                >
                  {t('roles.manager')}:
                </strong>{' '}
                {t('users.managerPermissions')}
              </li>
              <li>
                <strong
                  className={isDark ? 'text-slate-300' : 'text-slate-700'}
                >
                  {t('roles.admin')}:
                </strong>{' '}
                {t('users.adminPermissions')}
              </li>
            </ul>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/manager/users')}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-slate-900 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading
                ? t('users.sendingInvitation')
                : t('users.sendInvitation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserEdit: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<Partial<User>>({
    first_name: '',
    last_name: '',
    email: '',
    role: 'developer',
    employment_type: 'employee',
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const navigate = useNavigate();

  const toggleSpecialty = (specialtyId: number) => {
    setUser((prev) => {
      const current = prev.specialty_ids || [];
      return {
        ...prev,
        specialty_ids: current.includes(specialtyId)
          ? current.filter((x) => x !== specialtyId)
          : [...current, specialtyId],
      };
    });
  };

  const getAllowedRolesForEdit = (userBeingEdited: Partial<User>) => {
    const isContributorRole = CONTRIBUTOR_ROLE_VALUES.includes(
      userBeingEdited.role || '',
    );

    if (currentUser?.role === 'admin') {
      return [
        { value: 'developer', label: t('roles.developer') },
        { value: 'analyst', label: t('roles.analyst') },
        { value: 'designer', label: t('roles.designer') },
        { value: 'qa_engineer', label: t('roles.qa_engineer') },
        { value: 'manager', label: t('roles.manager') },
        { value: 'admin', label: t('roles.admin') },
      ];
    } else if (currentUser?.role === 'manager') {
      if (isContributorRole) {
        return [
          { value: 'developer', label: t('roles.developer') },
          { value: 'analyst', label: t('roles.analyst') },
          { value: 'designer', label: t('roles.designer') },
          { value: 'qa_engineer', label: t('roles.qa_engineer') },
        ];
      }
      return [];
    }
    return [];
  };

  const allowedRoles = getAllowedRolesForEdit(user);
  const canEditThisUser = allowedRoles.length > 0;

  useEffect(() => {
    if (id) {
      fetchUser();
    }
    specialtiesAPI    
      .getAll()
      .then((r) => setSpecialties(r.data || []))
      .catch(() => setSpecialties ([]));
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await usersAPI.getById(Number(id));
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setError(t('users.loadFailed'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await usersAPI.update(Number(id), {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
        employment_type: user.employment_type,
        active: user.active,
        salary: user.salary,
        hours_per_week: user.hours_per_week,
        province_code: user.province_code,
        specialty_ids: user.specialty_ids,
      });
      setSuccess(t('users.userUpdated'));
      setTimeout(() => {
        navigate('/manager/users');
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.error || t('users.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  if (user.role && !canEditThisUser) {
    return (
      <div className="p-4 sm:p-6">
        <div className="max-w-2xl mx-auto text-center">
          <h1
            className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('common.accessDenied')}
          </h1>
          <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {currentUser?.role === 'manager'
              ? t('users.managerCanOnlyEditDevelopers')
              : t('users.noPermissionToEdit')}
          </p>
          <button
            onClick={() => navigate('/manager/users')}
            className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {t('users.backToUsers')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/manager/users')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.chevronLeft}
          </button>
          <h1
            className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('users.editUser')}
          </h1>
        </div>

        {error && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-xl text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-xl text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`${cardClass} p-6 space-y-5`}>
            <div>
              <label className={labelClass}>{t('users.emailAddress')} *</label>
              <input
                type="email"
                required
                value={user.email || ''}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('auth.firstName')} *</label>
                <input
                  type="text"
                  required
                  value={user.first_name || ''}
                  onChange={(e) =>
                    setUser({ ...user, first_name: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{t('auth.lastName')} *</label>
                <input
                  type="text"
                  required
                  value={user.last_name || ''}
                  onChange={(e) =>
                    setUser({ ...user, last_name: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('users.role')} *</label>
                <select
                  required
                  value={user.role || 'developer'}
                  onChange={(e) => setUser({ ...user, role: e.target.value })}
                  className={inputClass}
                >
                  {allowedRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {currentUser?.role === 'manager' && (
                  <p
                    className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                  >
                    {t('users.managerCanOnlyManageDevelopers')}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>
                  {t('users.employmentType')} *
                </label>
                <select
                  required
                  value={user.employment_type || 'employee'}
                  onChange={(e) =>
                    setUser({
                      ...user,
                      employment_type: e.target.value as
                        | 'employee'
                        | 'contractor'
                        | 'intern'
                        | 'part_time'
                        | 'consultant',
                    })
                  }
                  className={inputClass}
                >
                  <option value="employee">{t('users.employee')}</option>
                  <option value="contractor">{t('users.contractor')}</option>
                  <option value="intern">{t('users.intern')}</option>
                  <option value="part_time">{t('users.part_time')}</option>
                  <option value="consultant">{t('users.consultant')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>{t('common.status')} *</label>
              <select
                required
                value={user.active ? 'active' : 'inactive'}
                onChange={(e) =>
                  setUser({ ...user, active: e.target.value === 'active' })
                }
                className={inputClass}
              >
                <option value="active">{t('projects.active')}</option>
                <option value="inactive">{t('projects.inactive')}</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                {t('users.province', { defaultValue: 'Province' })}
              </label>
              <select
                value={user.province_code || ''}
                onChange={(e) =>
                  setUser({ ...user, province_code: e.target.value || null })
                }
                className={inputClass}
              >
                <option value="">
                  {t('users.selectProvince', {
                    defaultValue: 'Select a province…',
                  })}
                </option>
                {PROVINCES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>
                  {t('users.salary', { defaultValue: 'Annual salary' })}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={user.salary ?? ''}
                  onChange={(e) =>
                    setUser({
                      ...user,
                      salary:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  {t('users.hoursPerWeek', { defaultValue: 'Hours per week' })}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={user.hours_per_week ?? ''}
                  onChange={(e) =>
                    setUser({
                      ...user,
                      hours_per_week:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t('users.skills', { defaultValue: 'Skills' })}
              </label>
              {specialties.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  {specialties.map((spec) => {
                    const selected = (user.specialty_ids || []).includes(spec.id);
                    return (
                      <button
                        key={spec.id}
                        type="button"
                        onClick={() => toggleSpecialty(spec.id)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          selected
                            ? 'bg-violet-500/20 border-violet-400 text-violet-200'
                            : isDark
                              ? 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                        }`}
                      >
                        {spec.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p
                  className={`mt-1 text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.noSkills', {
                    defaultValue: 'No skills yet — add some in Admin → Skills.',
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/manager/users')}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-slate-900 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? t('users.updating') : t('users.updateUser')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserView: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await usersAPI.getById(Number(id));
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setError(t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center">
          <h1
            className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('users.userNotFound')}
          </h1>
          <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {error || t('users.userNotFoundDescription')}
          </p>
          <button
            onClick={() => navigate('/manager/users')}
            className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {t('users.backToUsers')}
          </button>
        </div>
      </div>
    );
  }

  const getRoleColor = (role: string) => {
    if (isDark) {
      switch (role) {
        case 'admin':
          return 'bg-violet-500/20 text-violet-400 border border-violet-500/30';
        case 'manager':
          return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
        case 'developer':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    }
    switch (role) {
      case 'admin':
        return 'bg-violet-100 text-violet-700';
      case 'manager':
        return 'bg-cyan-100 text-cyan-700';
      case 'developer':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/manager/users')}
              className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              {Icons.chevronLeft}
            </button>
            <h1
              className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('users.userDetails')}
            </h1>
          </div>
          <Link
            to={`/manager/users/${user.id}/edit`}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.edit}
            <span>{t('users.editUser')}</span>
          </Link>
        </div>

        <div className={cardClass}>
          {/* User Header */}
          <div
            className={`px-6 py-5 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div
                className={`h-16 w-16 rounded-2xl flex items-center justify-center mx-auto sm:mx-0 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
              >
                <span
                  className={`text-xl font-bold ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  {user.first_name[0]}
                  {user.last_name[0]}
                </span>
              </div>
              <div className="text-center sm:text-left">
                <h2
                  className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {user.full_name}
                </h2>
                <p
                  className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {user.email}
                </p>
                <div className="flex items-center justify-center sm:justify-start mt-2 gap-2 flex-wrap">
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getRoleColor(user.role)}`}
                  >
                    {roleLabel(t, user.role)}
                  </span>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                      user.employment_type === 'contractor'
                        ? isDark
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-amber-100 text-amber-700'
                        : isDark
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-cyan-100 text-cyan-700'
                    }`}
                  >
                    {user.employment_type === 'contractor'
                      ? t('users.contractor')
                      : employmentTypeLabel(t, user.employment_type)}
                  </span>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${
                      user.active
                        ? isDark
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-emerald-100 text-emerald-700'
                        : isDark
                          ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                          : 'bg-rose-100 text-rose-700'
                    }`}
                  >
                    {user.active
                      ? t('projects.active')
                      : t('projects.inactive')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="px-6 py-5">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <dt
                  className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.organization')}
                </dt>
                <dd
                  className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {user.organization?.name}
                </dd>
              </div>
              <div>
                <dt
                  className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.role')}
                </dt>
                <dd
                  className={`mt-1 text-sm capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {roleLabel(t, user.role)}
                </dd>
              </div>
              <div>
                <dt
                  className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.employmentType')}
                </dt>
                <dd
                  className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {employmentTypeLabel(t, user.employment_type)}
                </dd>
              </div>
              <div>
                <dt
                  className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('common.status')}
                </dt>
                <dd
                  className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {user.active ? t('projects.active') : t('projects.inactive')}
                </dd>
              </div>
              <div>
                <dt
                  className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.memberSince')}
                </dt>
                <dd
                  className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {user.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : t('common.notAvailable')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserManagement: React.FC = () => {
  return (
    <Routes>
      <Route index element={<UserList />} />
      <Route path="invite" element={<InviteUser />} />
      <Route path=":id" element={<UserView />} />
      <Route path=":id/edit" element={<UserEdit />} />
    </Routes>
  );
};

export default UserManagement;
